import React, { useCallback, useMemo } from 'react'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
// react-grid-layout v2: use the legacy wrapper for 100% v1-compatible runtime
// API (data-grid pattern via child keys + WidthProvider HOC + responsive
// `layouts`). The native v2 API would require explicit per-item layout props
// and the useContainerWidth hook. `Layouts` was renamed to `ResponsiveLayouts`.
import type { Layout, ResponsiveLayouts as Layouts } from 'react-grid-layout/legacy'
import { Responsive, WidthProvider } from 'react-grid-layout/legacy'

import Actions, { useDispatch, useSelector } from '../../../Flux/index.js'
import { visualizationSlice } from '../../../Flux/Slice/Visualization/VisualizationSlice.js'
import type { State } from '../../../Flux/Slice/index.js'
import { selectAllSinks } from '../../../Flux/Slice/Visualization/Selectors.js'
import type { GridLayouts, SinkConfig, SinkId, SinkPanelId, SourceId } from '../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import SinkContainer from './SinkContainer.js'
import type { SinkTypeKey } from './Sinks/SinkRegistry.js'
import { SINK_REGISTRY } from './Sinks/SinkRegistry.js'
import { switchSinkKind } from './Sinks/ChartTypeSwitch.js'
import { seedLayoutsWithDefaults } from './SinkSeeder.js'
import { selectSelectedBucketsBySource } from './Sinks/Selector/BucketsBySourceSelector.ts'

const ResponsiveGrid = WidthProvider(Responsive)

type Panel = {
  panelId: SinkPanelId
  title: string
  sourceId: SourceId
  sinkId: SinkId
  bucketKey?: string
  Component: React.ComponentType<{ sourceId: SourceId; sinkId: SinkId; config: unknown; bucketKey?: string }>
  config: unknown
  kindKey: keyof typeof SINK_REGISTRY
}

interface Props {
  activeSourceId: SourceId | null
  editing: boolean
}
export default function SinksGrid(props: Props) {
  type AnySinkComponent = React.ComponentType<{
    sourceId: SourceId
    sinkId: SinkId
    config: unknown
    bucketKey?: string
  }>

  const dispatch = useDispatch()
  const vizActions = Actions[visualizationSlice.name]

  const layouts = useSelector((s: typeof State) => s.visualization.grid.layouts) as GridLayouts
  const items = useSelector((s: typeof State) => selectAllSinks(s))
  const selectedBucketsBySource = useSelector((s: typeof State) => selectSelectedBucketsBySource(s))

  const panels: Panel[] = useMemo(() => {
    const acc: Panel[] = []
    for (const { sourceId, sinkId, config } of items) {
      const reg = SINK_REGISTRY[config.kind as keyof typeof SINK_REGISTRY]
      if (!reg) continue

      if (reg.renderMode === 'multi-bucket') {
        acc.push({
          panelId: sinkId,
          title: config.title ?? config.kind,
          sourceId,
          sinkId,
          Component: reg.component as AnySinkComponent,
          config,
          kindKey: config.kind
        })
      } else {
        const selected = selectedBucketsBySource[sourceId] ?? []
        for (const b of selected) {
          acc.push({
            panelId: `${sinkId}:${b.key}`,
            title: `${config.title ?? config.kind} · ${b.label}`,
            sourceId,
            sinkId,
            bucketKey: b.key,
            Component: reg.component as AnySinkComponent,
            config,
            kindKey: config.kind
          })
        }
      }
    }
    return acc
  }, [items, selectedBucketsBySource])

  // ✅ Memoize panelMeta (prevents seedLayoutsWithDefaults from running every render)
  const panelMeta = useMemo(() => panels.map((p) => ({ panelId: p.panelId, kindKey: p.kindKey })), [panels])

  const seeded = useMemo(() => seedLayoutsWithDefaults(layouts, panelMeta), [layouts, panelMeta])

  const onLayoutChange = useCallback(
    (_current: Layout, all: Layouts) => {
      dispatch(visualizationSlice.actions.updateGridLayouts({ layouts: all as GridLayouts }))
    },
    [dispatch]
  )

  // The grid also reports layouts on mount, so only a drag or resize counts as the user editing the workspace.
  const onUserLayoutEdit = useCallback(() => dispatch(visualizationSlice.actions.releaseAutosaveHold()), [dispatch])

  const remove = useCallback(
    (sourceId: SourceId, sinkId: SinkId) => {
      dispatch(vizActions.unregisterSink({ sourceId, sinkId }))
      dispatch(visualizationSlice.actions.removePanelsBySink({ sinkId }))
    },
    [dispatch, vizActions]
  )

  const changeKind = useCallback(
    (sourceId: SourceId, config: SinkConfig, kind: SinkTypeKey) => {
      dispatch(vizActions.registerSink({ sourceId, sinkconfig: switchSinkKind(config, kind) }))
    },
    [dispatch, vizActions]
  )

  const renamePanel = useCallback(
    (sourceId: string, sinkId: string, newTitle: string) => {
      dispatch(vizActions.renamePanel({ sourceId, sinkId, name: newTitle }))
    },
    [dispatch, vizActions]
  )

  if (panels.length === 0) {
    return <div className="rounded-lg bg-base-100 shadow p-8 text-sm opacity-80">No sinks yet. Add one from the sidebar.</div>
  }

  return (
    <ResponsiveGrid
      className="layout"
      layouts={seeded}
      breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
      cols={{ lg: 12, md: 10, sm: 8, xs: 6, xxs: 4 }}
      rowHeight={30}
      margin={[12, 12]}
      containerPadding={[0, 0]}
      compactType="vertical"
      preventCollision={false}
      measureBeforeMount={false}
      onLayoutChange={onLayoutChange}
      onDragStop={onUserLayoutEdit}
      onResizeStop={onUserLayoutEdit}
      draggableHandle=".drag-handle"
      isDraggable={props.editing}
      isResizable={props.editing}
      resizeHandles={props.editing ? ['se'] : []}
    >
      {panels.map(({ panelId, title, sourceId, sinkId, Component, config, bucketKey, kindKey }) => (
        <div key={panelId} className="h-full min-w-0">
          <SinkContainer
            isHighlighted={props.activeSourceId === sourceId}
            sourceId={sourceId}
            sinkId={sinkId}
            title={title}
            onRemove={() => remove(sourceId, sinkId)}
            onRenamePanel={(newTitle) => renamePanel(sourceId, sinkId, newTitle)}
            onChangeKind={(kind) => changeKind(sourceId, config as SinkConfig, kind)}
            editing={props.editing}
            kindKey={kindKey}
          >
            <div className="h-full min-h-[240px]">
              <Component sourceId={sourceId} sinkId={sinkId} config={config} bucketKey={bucketKey} />
            </div>
          </SinkContainer>
        </div>
      ))}
    </ResponsiveGrid>
  )
}
